import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, Plus, Upload } from "lucide-react";

export const Route = createFileRoute("/admin/categories")({
  component: AdminCategories,
});

type Category = {
  id: string;
  slug: string;
  label: string;
  position: number;
  enabled: boolean;
  icon_url: string;
};

const empty: Omit<Category, "id"> = {
  slug: "",
  label: "",
  position: 0,
  enabled: true,
  icon_url: "",
};

function AdminCategories() {
  const [items, setItems] = useState<Category[]>([]);
  const [form, setForm] = useState<Omit<Category, "id">>(empty);
  const [uploading, setUploading] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("position");
    if (error) toast.error(error.message);
    else setItems((data as Category[]) ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const uploadIcon = async (file: File, target: "form" | string) => {
    setUploading(target);
    const ext = file.name.split(".").pop() || "png";
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage
      .from("category-icons")
      .upload(path, file, { contentType: file.type, cacheControl: "31536000" });
    if (error) {
      setUploading(null);
      return toast.error(error.message);
    }
    const { data } = supabase.storage.from("category-icons").getPublicUrl(path);
    if (target === "form") {
      setForm((f) => ({ ...f, icon_url: data.publicUrl }));
    } else {
      const { error: upErr } = await supabase
        .from("categories")
        .update({ icon_url: data.publicUrl })
        .eq("id", target);
      if (upErr) toast.error(upErr.message);
      else load();
    }
    setUploading(null);
    toast.success("Icon uploaded");
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const slug = form.slug.trim().toLowerCase().replace(/\s+/g, "-");
    if (!slug || !form.label.trim()) return toast.error("Slug and label required");
    const { error } = await supabase.from("categories").insert({ ...form, slug });
    if (error) return toast.error(error.message);
    toast.success("Category added");
    setForm(empty);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this category?")) return;
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const toggle = async (c: Category) => {
    const { error } = await supabase
      .from("categories")
      .update({ enabled: !c.enabled })
      .eq("id", c.id);
    if (error) return toast.error(error.message);
    load();
  };

  const rename = async (c: Category, label: string) => {
    const { error } = await supabase.from("categories").update({ label }).eq("id", c.id);
    if (error) return toast.error(error.message);
    load();
  };

  const reorder = async (c: Category, position: number) => {
    const { error } = await supabase
      .from("categories")
      .update({ position })
      .eq("id", c.id);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="rounded-lg border border-border p-5 space-y-3 bg-card">
        <h2 className="text-lg font-medium">Add category</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            required
            placeholder="Slug (e.g. tops)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="border border-border px-3 py-2 rounded text-sm bg-background"
          />
          <input
            required
            placeholder="Label (e.g. Tops)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="border border-border px-3 py-2 rounded text-sm bg-background"
          />
          <input
            type="number"
            placeholder="Position"
            value={form.position}
            onChange={(e) => setForm({ ...form, position: Number(e.target.value) })}
            className="border border-border px-3 py-2 rounded text-sm bg-background"
          />
        </div>
        <div className="flex items-center gap-3">
          {form.icon_url ? (
            <img src={form.icon_url} alt="" className="h-12 w-12 rounded-full object-cover bg-muted" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-muted" />
          )}
          <label className="inline-flex items-center gap-2 text-sm border border-border px-3 py-2 rounded cursor-pointer">
            <Upload className="h-4 w-4" />
            {uploading === "form" ? "Uploading..." : "Upload icon"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadIcon(f, "form");
              }}
            />
          </label>
        </div>
        <button className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 text-sm rounded">
          <Plus className="h-4 w-4" /> Add category
        </button>
      </form>

      <div className="rounded-lg border border-border divide-y divide-border bg-card">
        {items.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground">No categories yet.</p>
        )}
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-4">
            <label className="cursor-pointer shrink-0">
              {c.icon_url ? (
                <img src={c.icon_url} alt="" className="h-10 w-10 rounded-full object-cover bg-muted" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadIcon(f, c.id);
                }}
              />
            </label>
            <input
              type="number"
              value={c.position}
              onChange={(e) => reorder(c, Number(e.target.value))}
              className="w-16 border border-border px-2 py-1 rounded text-sm bg-background"
            />
            <input
              value={c.label}
              onChange={(e) =>
                setItems((prev) =>
                  prev.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)),
                )
              }
              onBlur={(e) => rename(c, e.target.value)}
              className="flex-1 border border-border px-2 py-1 rounded text-sm bg-background"
            />
            <span className="text-xs text-muted-foreground w-24 truncate">{c.slug}</span>
            <button
              onClick={() => toggle(c)}
              className="text-xs px-2 py-1 border border-border rounded"
            >
              {c.enabled ? "Enabled" : "Hidden"}
            </button>
            <button
              onClick={() => remove(c.id)}
              className="text-destructive p-2 hover:bg-destructive/10 rounded"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
